import { useEffect, useState } from 'react';

import { RESPONSIVE_BREAKPOINTS } from '../constants';
import { ResponsiveMode } from '../types';

/**
 * Hook to detect current responsive mode based on screen width
 */
export function useResponsiveMode(): ResponsiveMode {
  const [mode, setMode] = useState<ResponsiveMode>(() => {
    // Server-side rendering safe initialization
    if (typeof window === 'undefined') {
      return ResponsiveMode.DESKTOP;
    }

    const width = window.innerWidth;
    if (width <= RESPONSIVE_BREAKPOINTS.mobile) {
      return ResponsiveMode.MOBILE;
    } else if (width <= RESPONSIVE_BREAKPOINTS.tablet) {
      return ResponsiveMode.TABLET;
    } else {
      return ResponsiveMode.DESKTOP;
    }
  });

  useEffect(() => {
    function updateMode() {
      const width = window.innerWidth;
      let newMode: ResponsiveMode;

      if (width <= RESPONSIVE_BREAKPOINTS.mobile) {
        newMode = ResponsiveMode.MOBILE;
      } else if (width <= RESPONSIVE_BREAKPOINTS.tablet) {
        newMode = ResponsiveMode.TABLET;
      } else {
        newMode = ResponsiveMode.DESKTOP;
      }

      setMode(newMode);
    }

    window.addEventListener('resize', updateMode);

    // Call once to set initial state
    updateMode();

    return () => window.removeEventListener('resize', updateMode);
  }, []);

  return mode;
}

/**
 * Hook to get responsive configuration for the current mode
 */
export function useResponsiveConfig<T>(responsiveConfig: Partial<Record<ResponsiveMode, T>>, defaultConfig: T): T {
  const mode = useResponsiveMode();

  return responsiveConfig[mode] || defaultConfig;
}

/**
 * Hook to check if current screen size matches a specific responsive mode
 */
export function useIsResponsiveMode(targetMode: ResponsiveMode): boolean {
  const currentMode = useResponsiveMode();
  return currentMode === targetMode;
}

/**
 * Hook to check if screen is mobile-sized
 */
export function useIsMobile(): boolean {
  return useIsResponsiveMode(ResponsiveMode.MOBILE);
}

/**
 * Hook to check if screen is tablet-sized
 */
export function useIsTablet(): boolean {
  return useIsResponsiveMode(ResponsiveMode.TABLET);
}

/**
 * Hook to check if screen is desktop-sized
 */
export function useIsDesktop(): boolean {
  return useIsResponsiveMode(ResponsiveMode.DESKTOP);
}
